import { Flex, Typography, useModal } from '@aloft/toolkit-sui';
import { Textarea } from '@aloft/toolkit-sui/chakra';

import { useCallback, useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import type { BlockerFunction } from 'react-router-dom';

import { ValidationKey } from '../../../hooks/validation/types.ts';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { setBasementAreaDisclosureCommentary } from '../../../redux/features/commentarySlice.ts';
import { selectCommentary } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { resolveBasementAreaDisclosureNote } from '../../../utils/basementAreaDisclosure.ts';

export const BasementAreaDisclosureModal = () => {
  const dispatch = useAppDispatch();
  const { Modal, isModalOpen, openModal, closeModal } = useModal();
  const { errors } = useAppraisifyValidation();
  const isDisclosureRequired = Boolean(errors.Comparables[ValidationKey.BasementAreaDisclosure]);
  const { hasArvOption } = useAppSelector(selectOrder);
  const { reconciliation } = useAppSelector(selectCommentary);
  const savedNote = resolveBasementAreaDisclosureNote(reconciliation, hasArvOption);
  const [note, setNote] = useState(savedNote);

  const shouldBlock: BlockerFunction = useCallback(
    ({ currentLocation, nextLocation }) =>
      isDisclosureRequired && currentLocation.pathname !== nextLocation.pathname,
    [isDisclosureRequired],
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === 'blocked') openModal();
  }, [blocker.state, openModal]);

  useEffect(() => {
    setNote(savedNote);
  }, [savedNote]);

  const handleSave = () => {
    dispatch(setBasementAreaDisclosureCommentary({ note, hasArvOption }));
    closeModal();
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const handleClose = () => {
    closeModal();
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleClose}
      title="Basement Area Disclosure"
      primaryAction={{ text: 'Save', onClick: handleSave }}
      secondaryAction={{ text: 'Close', onClick: handleClose }}
    >
      <Flex flexDirection="column" gap={3}>
        <Typography variant="body1">
          One or more selected properties contain a partial or finished basement.
        </Typography>
        <Typography variant="body1">
          <b>ANSI Measurement Standard</b>: If any part of a level is below the adjacent ground line
          (grade), the entire level must be classified as &quot;Below-Grade&quot; — even if it is
          fully finished or walk-out ready.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Please describe how you are accounting for this below-grade square footage, particularly
          if the finished area is not separated out in the MLS or public records:
        </Typography>
        <Textarea resize="none" value={note} onChange={(event) => setNote(event.target.value)} />
        <Typography variant="body2" color="textTertiary">
          Note: This response will be appended to your Reconciliation Commentary.
        </Typography>
      </Flex>
    </Modal>
  );
};
